import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { capabilitiesForRole, normalizeRole, type CapabilityKey } from './permissions';
import { clearSession, getSession } from '@/lib/session';
import { getHomePathForRole } from '@/utils/roleRouting';
import { toast } from '@/lib/toast';

type ProtectedRouteProps = {
  requiredCapability?: CapabilityKey;
  children?: JSX.Element;
};

function getOficinaBlockedMessage(pathname: string): string | null {
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/tracking')) {
    return 'No tienes acceso a Dashboard';
  }
  if (
    pathname.startsWith('/operaciones') ||
    pathname.startsWith('/entradas') ||
    pathname.startsWith('/salidas') ||
    pathname.startsWith('/salida') ||
    pathname.startsWith('/transferencia') ||
    pathname.startsWith('/conteo')
  ) {
    return 'No tienes acceso a Operaciones';
  }
  return null;
}

export function ProtectedRoute({ requiredCapability, children }: ProtectedRouteProps) {
  const location = useLocation();
  const session = typeof window !== 'undefined' ? getSession() : null;

  if (!session?.token || !session?.user) {
    if (typeof window !== 'undefined') {
      clearSession();
    }
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const role = normalizeRole(session.user.role);
  const capabilities = capabilitiesForRole(role);

  if (requiredCapability && !capabilities[requiredCapability]) {
    const fallback = getHomePathForRole(role);
    if (role === 'OFICINA') {
      const message = getOficinaBlockedMessage(location.pathname);
      if (message) {
        toast(message, 'warning');
      }
    }
    return <Navigate to={fallback} replace />;
  }

  return children ?? <Outlet />;
}

export default ProtectedRoute;
